export const IMAGE_STORY_DURATION = 5000

export const INSTAGRAM_STORIES_SEEN_KEY = 'agm-instagram-stories-seen'

export const INSTAGRAM_STORIES_CLICKED_KEY = 'agm-instagram-stories-clicked'

export const INSTAGRAM_STORIES_HINT_KEY = 'agm-instagram-stories-swipe-hint'

export const INSTAGRAM_STORIES_OPENED_KEY = 'agm-instagram-stories-opened-at'

export const ORDER_WINDOW_DAYS = 7

export const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/agm_vision/'

export const SWIPE_DISTANCE = 40

const MINUTE = 60000

const HOUR = 60 * MINUTE

const DAY = 24 * HOUR

const safely = fn => {
  try {
    return fn()
  } catch {
    return null
  }
}

const textOf = value =>
  typeof value === 'string' && value !== '' ? value : null

const imageOf = image =>
  image && typeof image === 'object' && textOf(image.url) ? image : null

const storyOf = story => ({
  id: story?.id,
  video: story?.media_type === 'VIDEO',
  image: imageOf(story?.image),
  videoUrl: textOf(story?.video_url),
  previewUrl: textOf(story?.preview_video_url),
  permalink: textOf(story?.permalink),
  publishedAt: Number.isNaN(Date.parse(story?.published_at))
    ? null
    : story.published_at,
})

export function instagramStories(data) {
  return (Array.isArray(data) ? data : [])
    .map(storyOf)
    .filter(
      story =>
        Number.isInteger(story.id) &&
        story.publishedAt &&
        (story.video ? story.videoUrl : story.image)
    )
}

export function hasStoryToAnnounce(stories, seenIds, clickedIds) {
  return stories.some(
    story => !seenIds.includes(story.id) && !clickedIds.includes(story.id)
  )
}

export function firstUnseenIndex(stories, seenIds) {
  const index = stories.findIndex(story => !seenIds.includes(story.id))
  return index === -1 ? 0 : index
}

export function rememberSeen(seenIds, id, stories) {
  const current = stories.map(story => story.id)
  return [...new Set([...seenIds, id])].filter(seen => current.includes(seen))
}

export function swipeDirection(deltaX, deltaY) {
  if (
    Math.abs(deltaX) < SWIPE_DISTANCE ||
    Math.abs(deltaX) <= Math.abs(deltaY)
  ) {
    return 0
  }
  return deltaX < 0 ? 1 : -1
}

export function storyAge(publishedAt, now = new Date()) {
  const elapsed = Math.max(0, now.getTime() - Date.parse(publishedAt))
  if (elapsed >= HOUR) {
    return { value: Math.floor(elapsed / HOUR), unit: 'hour' }
  }
  return { value: Math.floor(elapsed / MINUTE), unit: 'minute' }
}

export function formatStoryAge(publishedAt, locale, now = new Date()) {
  const { value, unit } = storyAge(publishedAt, now)
  return (
    safely(() =>
      new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto',
        style: 'short',
      }).format(-value, unit)
    ) || ''
  )
}

const readIds = key => {
  if (typeof window === 'undefined') return []
  const ids = safely(() => JSON.parse(window.localStorage.getItem(key)))
  return Array.isArray(ids) ? ids.filter(Number.isInteger) : []
}

const writeIds = (key, ids) => {
  if (typeof window === 'undefined') return
  safely(() => window.localStorage.setItem(key, JSON.stringify(ids)))
}

export const readSeenStories = () => readIds(INSTAGRAM_STORIES_SEEN_KEY)

export const writeSeenStories = ids => writeIds(INSTAGRAM_STORIES_SEEN_KEY, ids)

export const readClickedStories = () => readIds(INSTAGRAM_STORIES_CLICKED_KEY)

export const writeClickedStories = ids =>
  writeIds(INSTAGRAM_STORIES_CLICKED_KEY, ids)

export const hasSeenSwipeHint = () =>
  typeof window === 'undefined' ||
  safely(() => window.localStorage.getItem(INSTAGRAM_STORIES_HINT_KEY)) === '1'

export const rememberSwipeHint = () => {
  if (typeof window === 'undefined') return
  safely(() => window.localStorage.setItem(INSTAGRAM_STORIES_HINT_KEY, '1'))
}

export function recentOpening(value, now = Date.now()) {
  const at = Date.parse(value)
  return Number.isNaN(at) || at > now || now - at > ORDER_WINDOW_DAYS * DAY
    ? null
    : new Date(at).toISOString()
}

export function openingToSend(opening, cart, now = Date.now()) {
  const recent = recentOpening(opening, now)
  if (!recent || !cart?.id || cart.ordered_at) return null
  const known = Date.parse(cart.instagram_stories_opened_at)
  return Number.isNaN(known) || known < Date.parse(recent) ? recent : null
}

export const readStoriesOpening = (now = Date.now()) => {
  if (typeof window === 'undefined') return null
  const stored = safely(() =>
    window.localStorage.getItem(INSTAGRAM_STORIES_OPENED_KEY)
  )
  const recent = recentOpening(stored, now)
  if (stored && !recent) {
    safely(() => window.localStorage.removeItem(INSTAGRAM_STORIES_OPENED_KEY))
  }
  return recent
}

export const rememberStoriesOpening = (now = new Date()) => {
  if (typeof window === 'undefined') return null
  const opening = now.toISOString()
  safely(() =>
    window.localStorage.setItem(INSTAGRAM_STORIES_OPENED_KEY, opening)
  )
  return opening
}
